import { getFileNameExtension, isFileCsvOrGpc } from "@components/fileUploader/File.utils";
import { isValidDate } from "@components/inputs/date/utils";
import { ifAny, IGetValueArgs } from "@components/smart/FieldInfo";
import {
    AttachmentsAdditionalProperties,
    AttachmentsDef,
    CurrencyDef,
    ExchangeRatePerUnit,
    getCommonFilterDefs,
    getCommonTableColumnsDefs,
    NoteFieldDef,
    SymbolConstantDef,
    SymbolSpecificDef,
    SymbolVariableDef
} from "@components/smart/GeneralFieldDefinition";
import { ISmartBankAccountFilterCustomData } from "@components/smart/smartBankAccountFilter/SmartBankAccountFilter.utils";
import {
    FilterBarGroup,
    getDefaultFilterGroupDef,
    IFilterGroupDef
} from "@components/smart/smartFilterBar/SmartFilterBar.types";
import { ISummaryItem } from "@components/smart/smartSummaryItem/SmartSummaryItem";
import {
    BankStatementEntity,
    BankTransactionEntity,
    BusinessPartnerEntity,
    CompanyBankAccountEntity,
    DocumentBusinessPartnerEntity,
    DocumentEntity,
    EntitySetName,
    EntityTypeName,
    IBankStatementEntity,
    IBankTransactionEntity,
    IFileMetadataEntity
} from "@odata/GeneratedEntityTypes";
import { ClearedStatusCode, CurrencyCode, DocumentTypeCode } from "@odata/GeneratedEnums";
import { IFormatOptions } from "@odata/OData.utils";
import { BankCoreBusinessPartnerAdditionalProperties } from "@pages/businessPartner/BusinessPartner.utils";
import {
    accountAdditionalFields,
    generalBankAccountFormatter,
    getBankDependentFields,
    getCompanyBankAccountNavigationFieldDefinition
} from "@utils/BankUtils";
import { getCompanyCurrency } from "@utils/CompanyUtils";
import { isDefined } from "@utils/general";
import i18next from "i18next";

import { IAppContext } from "../../../contexts/appContext/AppContext.types";
import {
    BankAccountType,
    BasicInputSizes,
    FastEntryInputSizes,
    FieldType,
    Sort,
    TextAlign,
    ToolbarItemType,
    ValidatorType
} from "../../../enums";
import { TRecordAny, TValue } from "../../../global.types";
import { Model } from "../../../model/Model";
import BindingContext, { createPath } from "../../../odata/BindingContext";
import CurrencyType, { formatCurrency, formatCurrencyVariableDecimals } from "../../../types/Currency";
import { getUtcDate } from "../../../types/Date";
import { currencyScaleParser } from "../../../types/Number";
import { IFormDef } from "../../../views/formView/Form";
import { FormStorage } from "../../../views/formView/FormStorage";
import { ISplitPageTableDef } from "../../../views/table/TableView.utils";
import { addBankAccountTableDefs } from "../../documents/DocumentCommonDefs";
import { setDefByEntityType } from "../../getDefByEntityType";
import {
    getNumberOursSummaryDef,
    getNumberRangeFieldDefs,
    NumberRangeAdditionalProperties
} from "../../numberRange/NumberRange.utils";
import { getItemBreadCrumbsText, IDefinition, IGetDefinition } from "../../PageUtils";
import { IBankStatementsCustomData, paymentDocDateValidator } from "../bankTransactions/BankTransactions.utils";
import { getTransactionsRender, isFieldTransactionDisabled } from "./BankStatements.utils";
import BankStatementsFormView from "./BankStatementsFormView";

export const GROUP_TRANSACTIONS_ID = "Transactions";

const hasPostedTransaction = (args: IGetValueArgs): boolean => {
    const transactions = args.data.Transactions || [];

    const postedTransaction = transactions.find((t: IBankTransactionEntity) => {
        return t.ClearedStatusCode === ClearedStatusCode.Cleared || t.ClearedStatusCode === ClearedStatusCode.PartiallyCleared;
    });

    return !!postedTransaction;
};

export const APPLY_BANKFILE_IMPORT = "applybankfileimport";

export const OpeningBalancePath = BindingContext.localContext("OpeningBalance");
export const ClosingBalancePath = BindingContext.localContext("ClosingBalance");

const getBankStatementTransactionCurrency = ({ BankAccount }: IBankStatementEntity, context: IAppContext): CurrencyCode =>
    (BankAccount?.TransactionCurrencyCode ?? BankAccount?.TransactionCurrency?.Code ?? getCompanyCurrency(context)) as CurrencyCode;

const bankStatementAmountSummaryFormatter = (value: TValue, args: IFormatOptions) => {
    const currency = getBankStatementTransactionCurrency(args.entity, args.storage.context);
    return formatCurrencyVariableDecimals((value as number) ?? 0, currency);
};

const bankStatementAmountTableFormatter = (value: TValue, args: IFormatOptions) => {
    const currency = getBankStatementTransactionCurrency(args.entity, args.storage.context);
    return formatCurrency((value as number) ?? 0, currency);
};

export const getCoreDef = (type: DocumentTypeCode) => {
    const _isBank = type === DocumentTypeCode.BankTransaction;
    const filterBarDef: IFilterGroupDef[] = [{
        ...getDefaultFilterGroupDef(FilterBarGroup.Filters),
        isValueHelp: true,
        defaultFilters: [
            "NumberOurs",
            "DateFrom",
            "Balance/TransactionsCount",
            "Balance/TransactionsTurnover",
            "BankAccount/AccountNumber",
            "BankAccount/Name"
        ],
        filterDefinition: {
            NumberOurs: {},
            Note: {},
            NumberTheirs: {},
            DateFrom: {},
            "BankAccount/Name": {
                label: i18next.t("Banks:Statements.AccountName")
            },
            ...getCommonFilterDefs(),
            "Balance/TransactionsCount": {},
            "Balance/TransactionsTurnover": {},
            "Balance/CreditTurnover": {
                formatter: bankStatementAmountTableFormatter,
                filter: {
                    groupByProperties: [
                        "BankAccount/TransactionCurrencyCode"
                    ]
                }
            },
            "Balance/DebitTurnover": {
                formatter: bankStatementAmountTableFormatter,
                filter: {
                    groupByProperties: [
                        "BankAccount/TransactionCurrencyCode"
                    ]
                }
            }
        }
    }];

    const table: ISplitPageTableDef = {
        filterBarDef,
        id: `${EntityTypeName.BankStatement}Table`,
        lockProperty: BankStatementEntity.Locks,
        initialSortBy: [
            { id: "DateFrom", sort: Sort.Desc }
        ],
        columns: [
            "NumberOurs",
            "BankAccount",
            "BankAccount/Name",
            "Balance/TransactionsCount",
            "Balance/TransactionsTurnover",
            "DateFrom"
        ],
        filter: "IsVisible eq true",
        columnDefinition: {
            Note: {},
            NumberTheirs: {},
            ...getCommonTableColumnsDefs(),
            NumberOurs: {
                additionalProperties: [{ id: "/Transactions" }]
            },
            BankAccount: {
                additionalProperties: accountAdditionalFields.concat({ id: "AccountNumber" }, { id: "Name" }, { id: "CompanyBankAccountTypeCode" }, { id: "PaymentServiceID" }),
                formatter: generalBankAccountFormatter,
                label: i18next.t("Banks:Statements.AccountNumber")
            },
            "BankAccount/Name": {
                label: i18next.t("Banks:Statements.AccountName")
            },
            DateFrom: {},
            "Balance/TransactionsCount": {},
            "Balance/TransactionsTurnover": {
                additionalProperties: [{ id: "/BankAccount/TransactionCurrencyCode" }],
                formatter: (val: TValue, args?: IFormatOptions) => {
                    const currency = args.item?.BankAccount?.TransactionCurrencyCode;
                    return formatCurrency(val as number, currency);
                }
            },
            "Balance/CreditTurnover": {
                formatter: bankStatementAmountTableFormatter,
                additionalProperties: [
                    { id: "/BankAccount/TransactionCurrency" }
                ]
            },
            "Balance/DebitTurnover": {
                formatter: bankStatementAmountTableFormatter,
                additionalProperties: [
                    { id: "/BankAccount/TransactionCurrency" }
                ]
            }
        },
        title: i18next.t("Banks:Statements.Title")
    };

    const isPassiveAccount = (args: IGetValueArgs) => {
        return args.storage.data.entity.BankAccount?.IsActive === false;
    };

    const isDialogStatement = (args: IGetValueArgs) => {
        const storage = args.storage as FormStorage<IBankStatementEntity, ISmartBankAccountFilterCustomData>;
        return storage.getCustomData().isStatementDialog;
    };

    const summary: ISummaryItem[] = [
        {
            id: BindingContext.localContext("TransactionCount"),
            formatter: (val: TValue, args: IFormatOptions) => {
                return args.storage.getCustomData<ISmartBankAccountFilterCustomData>().transactionCount || 0;
            },
            label: i18next.t("Banks:Statements.TransactionsCount")
        },
        {
            id: "Balance/CreditTurnover",
            label: _isBank ? i18next.t("Banks:Statements.CreditTurnover") : i18next.t("Banks:LoanStatement.CreditTurnover"),
            updateFromLiveValue: true,
            formatter: bankStatementAmountSummaryFormatter,
            color: "C_SEM_text_good"
        }, {
            id: "Balance/DebitTurnover",
            label: _isBank ? i18next.t("Banks:Statements.DebitTurnover") : i18next.t("Banks:LoanStatement.DebitTurnover"),
            updateFromLiveValue: true,
            formatter: bankStatementAmountSummaryFormatter,
            color: "C_SEM_text_bad"
        },
        {
            id: OpeningBalancePath,
            label: i18next.t("Banks:Statements.OpeningBalance"),
            textAlign: TextAlign.Right,
            updateFromLiveValue: true,
            formatter: (value: TValue, args: IFormatOptions) => {
                const { initialBalance } = args.storage.getCustomData<ISmartBankAccountFilterCustomData>();
                return bankStatementAmountSummaryFormatter(initialBalance, args);
            }
        }, {
            id: ClosingBalancePath,
            label: i18next.t("Banks:Statements.ClosingBalance"),
            textAlign: TextAlign.Right,
            updateFromLiveValue: true,
            additionalProperties: [{ id: "Balance/TransactionsTurnover" }],
            formatter: (value: TValue, args: IFormatOptions) => {
                const statementInitialBalance = args.storage.getCustomData<ISmartBankAccountFilterCustomData>().initialBalance ?? 0;
                const transactionsTurnover = args.storage.getEntity<IBankStatementEntity>().Balance?.TransactionsTurnover ?? 0;
                return bankStatementAmountSummaryFormatter(statementInitialBalance + transactionsTurnover, args);
            }
        }
    ];


    const bankAccountDef = getCompanyBankAccountNavigationFieldDefinition();

    const form: IFormDef = {
        id: `${EntityTypeName.BankStatement}Form`,
        summary: [getNumberOursSummaryDef("Banks"), ...summary],
        files: AttachmentsDef,
        translationFiles: getDefinitions.translationFiles,
        formControl: BankStatementsFormView,
        lockProperty: BankStatementEntity.Locks,
        isDeletable: true,
        getCustomAttachmentFileActions: (file: IFileMetadataEntity) => {
            const firstFileExtension = getFileNameExtension(file?.Name);

            if (isFileCsvOrGpc(firstFileExtension) && type === DocumentTypeCode.BankTransaction) {
                return [{
                    id: APPLY_BANKFILE_IMPORT,
                    label: i18next.t("Banks:Statements.ApplyImportFile"),
                    itemType: ToolbarItemType.Icon,
                    iconName: "Arrow"
                }];
            }
            return null;
        },
        getItemBreadCrumbText: (storage: Model) =>
            getItemBreadCrumbsText(storage, i18next.t("Banks:Statements:NewFormTitle"), storage.data.entity?.NumberOurs),
        title: "Banks:Statements.FormTitle",
        additionalProperties: [...NumberRangeAdditionalProperties, { id: "Transactions/TransactionAmount" },
            { id: "Transactions/SymbolVariable" },
            { id: "Transactions/DateBankTransaction" }, { id: `Transactions/${BankStatementEntity.Locks}` }, { id: "Transactions/PostedStatus" }, { id: "Transactions/PostedStatusCode" },
            // load properties that are add in BankStatementFormView.onBeforeSave,
            // so that during saving, compareEntities can recognize changed properties correctly,
            // and won't create unnecessary PATCH requests when not needed
            { id: "Transactions/Amount" },
            { id: "Transactions/ClearedStatusCode" },
            { id: "Transactions/BankTransactionTypeCode" },
            { id: "Transactions/Company" }, { id: "Transactions/TransactionAmount" },
            ...AttachmentsAdditionalProperties,
            { id: "BankAccount/BankStatementNumberRangeDefinition" },
            { id: `${BankStatementEntity.Locks}/CreatedBy` },
            { id: `${BankStatementEntity.Locks}/Type` }
        ],
        fieldDefinition: {
            ...getNumberRangeFieldDefs("Banks"),
            BankAccount: {
                ...bankAccountDef,
                label: i18next.t("Banks:Statements.AccountNumber"),
                isDisabled: ifAny(hasPostedTransaction, isPassiveAccount),
                isReadOnly: isDialogStatement,
                additionalProperties: accountAdditionalFields.concat([{ id: "Name" }, { id: "PaymentServiceID" }, { id: "IsActive" }, { id: "Currency/MinorUnit" }]),
                type: FieldType.ComboBox,
                width: BasicInputSizes.XXL,
                affectedFields: [{
                    id: "Transactions"
                }, {
                    id: "BankAccount/TransactionCurrency"
                }, {
                    id: BindingContext.localContext("Sum")
                }],
                fieldSettings: {
                    ...bankAccountDef.fieldSettings,
                    groups: [
                        { id: BankAccountType.Account },
                        { id: BankAccountType.ABA },
                        { id: BankAccountType.IBANOnly },
                        {
                            id: BankAccountType.Service,
                            title: i18next.t("Banks:Accounts.OtherServices")
                        }
                    ],
                    localDependentFields: getBankDependentFields()
                }
            },
            "BankAccount/TransactionCurrency": {
                type: FieldType.ComboBox,
                width: BasicInputSizes.S,
                fieldSettings: {
                    displayName: "Code",
                    shouldDisplayAdditionalColumns: true,
                    preloadItems: true
                },
                isReadOnly: true,
                additionalProperties: [{ id: "Name" }],
                columns: [
                    { id: "Code" },
                    { id: "Name" }
                ]
            },
            DateFrom: {
                isDisabled: hasPostedTransaction,
                width: "130px",
                defaultValue: () => {
                    return getUtcDate();
                },
                type: FieldType.Date
            },
            "Transactions/NumberOurs": {
                isReadOnly: true,
                width: BasicInputSizes.L,
                useForValidation: false,
                formatter: (val: TValue, args?: IFormatOptions) => {
                    if (!val) {
                        const NumberOurs = args.storage.getValueByPath(BankStatementEntity.NumberOurs);
                        const Order = args.storage.getValue(args.bindingContext.getParent().navigate(BankTransactionEntity.Order));
                        return `${NumberOurs}-${(Order ?? "").toString().padStart(6, "0")}`;
                    }
                    return val?.toString();
                }
            },
            "Transactions/DateBankTransaction": {
                isDisabled: isFieldTransactionDisabled,
                defaultValue: (args: IGetValueArgs) => {
                    const statement = args.storage.data.entity as IBankStatementEntity;
                    return statement.Transactions.reverse().find((item) => isValidDate(item.DateBankTransaction))?.DateBankTransaction ?? statement.DateFrom;
                },
                fieldSettings: {
                    workDate: (args: IGetValueArgs, obj: TRecordAny) => {
                        const rootDate = args.storage.data.entity.DateFrom;
                        return isValidDate(rootDate) ? rootDate : getUtcDate();
                    }
                },
                validator: {
                    type: ValidatorType.Custom,
                    settings: {
                        customValidator: paymentDocDateValidator
                    }
                },
                width: "150px"
            },
            "Transactions/TransactionCurrency": {
                isRequired: false,
                clearIfInvisible: false,
                isReadOnly: true,
                defaultValue: args => {
                    return args.storage.getValueByPath(createPath(BankStatementEntity.BankAccount, CompanyBankAccountEntity.TransactionCurrencyCode));
                }
            },
            "Transactions/TransactionAmount": {
                isDisabled: isFieldTransactionDisabled,
                width: FastEntryInputSizes.S,
                label: i18next.t("Banks:Transactions.Amount"),
                affectedFields: [{ id: BindingContext.localContext("Sum") }],
                parser: (value: TValue, args: IFormatOptions) => {
                    return currencyScaleParser(value as string, args.storage.data.entity.BankAccount?.TransactionCurrency);
                },
                fieldSettings: {
                    unit: (args: IGetValueArgs) => {
                        const currency = getBankStatementTransactionCurrency(args.storage.data.entity, args.storage.context);
                        return CurrencyType.getCurrencyUnit(currency);
                    }
                }
            },
            "Transactions/Amount": {
                isDisabled: isFieldTransactionDisabled,
                useForValidation: false,
                customizationData: {
                    useForCustomization: false
                },
                isRequired: false
            },
            "Transactions/Currency": {
                ...CurrencyDef,
                isDisabled: isFieldTransactionDisabled,
                customizationData: {
                    useForCustomization: false
                },
                defaultValue: args => getCompanyCurrency(args.storage.context)
            },
            "Transactions/ExchangeRatePerUnit": {
                ...ExchangeRatePerUnit,
                defaultValue: "",
                isDisabled: isFieldTransactionDisabled,
                width: FastEntryInputSizes.XS,
                label: i18next.t("Document:Form.CurrencyRate"),
                isVisible: (args: IGetValueArgs) => {
                    const currency = args.storage.data.entity.BankAccount?.TransactionCurrency?.Code;
                    return currency !== getCompanyCurrency(args.storage.context);
                }
            },
            "Transactions/PaymentInformation": {
                isDisabled: isFieldTransactionDisabled
            },
            "Transactions/SymbolVariable": {
                ...SymbolVariableDef,
                isDisabled: isFieldTransactionDisabled
            },
            [createPath(BankStatementEntity.Transactions, BankTransactionEntity.SymbolSpecific)]: {
                ...SymbolSpecificDef,
                isDisabled: isFieldTransactionDisabled
            },
            [createPath(BankStatementEntity.Transactions, BankTransactionEntity.SymbolConstant)]: {
                ...SymbolConstantDef,
                isDisabled: isFieldTransactionDisabled
            },
            Note: {
                ...NoteFieldDef
            },
            NumberTheirs: {}
        },
        groups: [{
            id: "noteGroup",
            title: i18next.t("Common:General.Note"),
            rows: [[{ id: "Note" }]]
        }, {
            id: "mainGroup",
            title: i18next.t("Common:General.Information"),
            rows: [[{ id: "DateFrom" }, { id: "BankAccount" }, { id: "BankAccount/TransactionCurrency" }]]
        }, {
            id: GROUP_TRANSACTIONS_ID,
            isVisible: (args: IGetValueArgs) => {
                const isNew = args.storage.data.bindingContext.isNew();
                const hideTransactions = (args.storage as FormStorage<IBankStatementEntity, IBankStatementsCustomData>).getCustomData().hideTransactions;
                // if we open this form in bank transaction pairing dialog (available only for creating new) we hade transactions
                return !(isNew && hideTransactions);
            },
            title: i18next.t("Banks:AutomaticPairingDialog.BankTransactionsPairing"),
            lineItems: {
                isReadOnly: isPassiveAccount,
                additionalFields: [{ id: "Amount" }, { id: "NumberOurs" }],
                collection: "Transactions",
                showLineNumbers: true,
                isTheOnlyItemRemovable: true,
                order: "Order",
                render: getTransactionsRender,
                columns: [
                    { id: "DateBankTransaction" }, { id: "SymbolVariable" }, { id: "TransactionAmount" }, { id: "ExchangeRatePerUnit" }, { id: "RemittanceInformation" }
                ]
            }
        }]
    };

    const definition: IDefinition = {
        entitySet: EntitySetName.BankStatements,
        table,
        form
    };

    addBankAccountTableDefs(definition);

    return definition;
};

export const getDefinitions: IGetDefinition = (): IDefinition => {
    const def = getCoreDef(DocumentTypeCode.BankTransaction);

    def.form.fieldDefinition = {
        ...def.form.fieldDefinition,
        "Transactions/RemittanceInformation": {
            isDisabled: isFieldTransactionDisabled,
            width: BasicInputSizes.XXL
        },
        "Transactions/BankInternalId": {
            isDisabled: isFieldTransactionDisabled
        },
        "Transactions/PaymentType": {
            isDisabled: isFieldTransactionDisabled,
            width: BasicInputSizes.L
        },
        "Transactions/BankAccountDescription": {
            isDisabled: isFieldTransactionDisabled,
            width: BasicInputSizes.L
        },
        "Transactions/BusinessPartner/Name": {
            additionalProperties: [...BankCoreBusinessPartnerAdditionalProperties.map(item => {
                return {
                    id: `/${BankStatementEntity.Transactions}/${item.id}`
                };
            }), { id: `/${createPath(BankStatementEntity.Transactions, DocumentEntity.BusinessPartner, DocumentBusinessPartnerEntity.BusinessPartner, BusinessPartnerEntity.Id)}` }],
            label: i18next.t("BusinessPartner:FormTitle"),
            isReadOnly: true,
            isRequired: false
        },
        "Transactions/BankAccount/AccountNumber": {
            isReadOnly: true,
            additionalProperties: [{ id: "/Transactions/BankAccount/BankCode" }],
            formatter: (val: TValue, args?: IFormatOptions) => {
                const item = args.storage.getValue(args.bindingContext.getParent());
                const bankCode = item?.BankCode;
                return isDefined(val) && isDefined(bankCode) ? `${val}/${bankCode}` : null;
            }
        }
    };

    def.form.additionalProperties = [
        ...def.form.additionalProperties, { id: "Transactions/RemittanceInformation" }
    ];


    return def;
};

getDefinitions.translationFiles = ["Banks", "Document", "BusinessPartner", "NumberRange"];
setDefByEntityType(EntityTypeName.BankStatement, getDefinitions);